import React from "react";
import { fmtInstant } from "../utils/dates";

const AVERAGE_FILL = "#64748b";

const isScore = value => typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 100;
const toTenths = value => Math.round(value * 10);
const fromTenths = value => value / 10;
const displayScore = value => Number.isInteger(value) ? String(value) : value.toFixed(1);

const ScoreRow = ({ id, label, score, color, T, outlined = false }) => {
  const available = score != null;
  return (
    <div style={{ marginBottom:8 }}>
      <div style={{ display:"grid", gridTemplateColumns:"minmax(0, 1fr) auto", gap:8, alignItems:"baseline", marginBottom:3 }}>
        <span data-testid={`${id}-label`} style={{ minWidth:0, fontSize:11, lineHeight:1.3, color:T.text, overflowWrap:"anywhere" }}>{label}</span>
        <span data-testid={`${id}-value`} style={{ fontSize:11, fontWeight:700, color: available ? T.text : T.muted, whiteSpace:"nowrap" }}>{available ? `${displayScore(score)}%` : "\u2014"}</span>
      </div>
      {available ? (
        <div style={{ height:8, borderRadius:999, background:"rgba(255,255,255,.08)", overflow:"hidden" }}>
          <div
            data-testid={`${id}-fill`}
            style={{
              height:"100%",
              width:`${score}%`,
              maxWidth:"100%",
              background:color,
              border:outlined && score > 0 ? `1px solid ${T.green}` : undefined,
              boxSizing:"border-box",
              borderRadius:999,
            }}
          />
        </div>
      ) : (
        <div data-testid={`${id}-empty`} style={{ height:8, borderRadius:999, border:`1px dashed ${T.border}`, background:"repeating-linear-gradient(90deg, rgba(255,255,255,.06) 0 6px, transparent 6px 12px)", opacity:.55 }} />
      )}
    </div>
  );
};

const CustomerComparisonCard = ({ score, benchmark, T }) => {
  const customerTenths = isScore(score) ? toTenths(score) : null;
  // The parent owns the publication/context validation. This lightweight
  // shape guard only keeps the presentation component safe for absent input.
  const averageTenths = isScore(benchmark?.aggregate_score) ? toTenths(benchmark.aggregate_score) : null;
  const customerScore = customerTenths == null ? null : fromTenths(customerTenths);
  const averageScore = averageTenths == null ? null : fromTenths(averageTenths);
  const hasBenchmark = averageTenths != null;
  const comparison = customerTenths != null && hasBenchmark
    ? customerTenths > averageTenths ? "above" : customerTenths < averageTenths ? "below" : "at"
    : null;
  const differenceTenths = comparison ? Math.abs(customerTenths - averageTenths) : null;
  const differenceLabel = comparison && differenceTenths !== 0
    ? `${displayScore(fromTenths(differenceTenths))} percentage point${differenceTenths === 10 ? "" : "s"}`
    : null;
  const customerColor = comparison === "above" ? T.green : comparison === "below" ? T.yellow : T.blue;

  return (
    <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.06)", borderRadius:10, padding:"10px 14px 4px", marginTop:12 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
        <div style={{ fontSize:10, color:T.muted, textTransform:"uppercase", letterSpacing:".06em", fontWeight:700 }}>How You Compare</div>
      </div>
      <ScoreRow id="customer" label="SecureShield Score" score={customerScore} color={customerColor} T={T} />
      {comparison && (
        <div data-testid="comparison-line" style={{ margin:"2px 0 12px", fontSize:11, color:T.text }}>
          {comparison === "at"
            ? "At the customer average."
            : <><strong data-testid="comparison-number">{differenceLabel}</strong><span data-testid="comparison-suffix"> {comparison} average</span></>}
        </div>
      )}
      {hasBenchmark ? (
        <>
          <ScoreRow id="average" label="SecureShield365 Customer Average" score={averageScore} color={AVERAGE_FILL} T={T} outlined />
          <div style={{ fontSize:10, color:T.muted, marginTop:-5, marginBottom:8 }}>Benchmark data checked {fmtInstant(benchmark.updated_at, { month:"numeric", day:"numeric", year:"numeric", timeZone:"UTC" })}</div>
        </>
      ) : <div style={{ fontSize:11, color:T.muted, marginBottom:8 }}>Customer average is not available right now.</div>}
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:T.muted, marginTop:2 }}>
        <span>0%</span><span>50%</span><span>100%</span>
      </div>
    </div>
  );
};

export default CustomerComparisonCard;
